import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import type { AccessFolder, AccessRequestResult, Loadable } from '../types'

/* ---------- pills ---------- */

export function Pill({ tone = 'neutral', children }: {
  tone?: 'neutral' | 'amber' | 'red' | 'accent'
  children: ReactNode
}) {
  return <span className={`pill pill--${tone}`}>{children}</span>
}

/* ---------- state card ----------
 * The core of the design system. Three encodings, no traffic light.
 * Locked is the MAJORITY state (41 of 86 for a typical user) so it is drawn
 * quietly — recessed ground, dashed edge, owner name as the payload — and it
 * still ends in an action, because a dead end is the one thing this must not do.
 */

export function StateCard({ folder, onRequest, onWho }: {
  folder: AccessFolder
  onRequest?: (f: AccessFolder) => void
  onWho?: (f: AccessFolder) => void
}) {
  const { state, access } = folder

  if (state === 'unknown') {
    return (
      <div className="state" style={{ borderLeft: '3px solid var(--amber)' }}>
        <span className="state__label">Could not check</span>
        <span className="state__name">{folder.name}</span>
        <span className="state__sub">
          Google did not answer for this folder. This is not the same as no access.
        </span>
        <div className="state__act"><Pill tone="amber">Retry pending</Pill></div>
      </div>
    )
  }

  if (state === 'not-visible') {
    return (
      <div className="state state--locked">
        <span className="state__label">Locked</span>
        <span className="state__name">{folder.name}</span>
        <span className="state__sub">{folder.accountableOwner}</span>
        <div className="state__act">
          <button className="btn btn--ghost" onClick={() => onRequest?.(folder)}>
            Request access →
          </button>
        </div>
      </div>
    )
  }

  const canFile = access === 'edit'
  return (
    <div className={`state ${canFile ? 'state--file' : 'state--read'}`}>
      <span className="state__label">{canFile ? 'You can file here' : 'You can read'}</span>
      <span className="state__name">{folder.name}</span>
      <span className="state__sub">{folder.grantedBy}</span>
      <div className="state__act" style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap' }}>
        <a
          className={`btn ${canFile ? 'btn--primary' : 'btn--secondary'}`}
          href={folder.webViewLink}
          target="_blank"
          rel="noreferrer"
        >
          {canFile ? 'Add files in Drive' : 'Open in Drive'} <span aria-hidden="true">↗</span>
        </a>
        {onWho && (
          <button className="btn btn--ghost" onClick={() => onWho(folder)}>Who else?</button>
        )}
      </div>
    </div>
  )
}

/* ---------- the state matrix, as components ----------
 * DESIGN.md specifies loading / empty / error for every screen. These are the
 * primitives so no screen ever falls back to a component-library default.
 */

export function LoadingCards({ count = 3, label }: { count?: number; label: string }) {
  return (
    <>
      <span className="sr-only" role="status">{label}</span>
      <div className="states" aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <div className="state" key={i}>
            <div className="skeleton" style={{ width: '38%', height: '.62rem' }} />
            <div className="skeleton" style={{ width: '82%', height: '1rem', marginTop: 6 }} />
            <div className="skeleton" style={{ width: '60%', height: '.72rem', marginTop: 6 }} />
            <div className="skeleton" style={{ width: '34%', height: 36, marginTop: 14, borderRadius: 4 }} />
          </div>
        ))}
      </div>
    </>
  )
}

export function EmptyBlock({ title, children, action }: {
  title: string; children: ReactNode; action?: ReactNode
}) {
  return (
    <div className="block block--sunk block--narrow">
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  )
}

export function ErrorBlock({ message, correlationId, onRetry }: {
  message: string; correlationId?: string; onRetry?: () => void
}) {
  return (
    <div className="block block--narrow" role="alert">
      <Pill tone="red">Something went wrong</Pill>
      <h3 style={{ marginTop: 'var(--s-sm)' }}>{message}</h3>
      <p>
        Nothing was changed. Try again, and if it keeps happening send this reference
        to the RMS Administrator.
      </p>
      <div style={{ display: 'flex', gap: 'var(--s-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
        {onRetry && <button className="btn btn--secondary" onClick={onRetry}>Try again</button>}
        {correlationId && <code className="state__sub">{correlationId}</code>}
      </div>
    </div>
  )
}

/* ---------- loadable hook ----------
 * Every async surface goes through this, so every screen has all four states
 * by construction rather than by remembering to write them.
 */

export function useLoadable<T>(
  load: () => Promise<T>,
  isEmpty: (d: T) => boolean,
  deps: unknown[] = [],
): [Loadable<T>, () => void] {
  const [state, setState] = useState<Loadable<T>>({ status: 'loading' })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let live = true
    setState({ status: 'loading' })
    load()
      .then((d) => { if (live) setState(isEmpty(d) ? { status: 'empty' } : { status: 'ready', data: d }) })
      .catch((e: Error & { correlationId?: string }) => {
        if (live) setState({ status: 'error', message: e.message, correlationId: e.correlationId })
      })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return [state, () => setNonce((n) => n + 1)]
}

/* ---------- access-request outcome ----------
 * Owner notification is not automated yet, so the honest outcome is: the
 * request is recorded, and here is who to email, with the email written for
 * you. The button is the action (accent); an interim contact is labelled as
 * such and never presented as the folder's owner.
 */

export type RequestOutcome = AccessRequestResult | { ok: false; delivered: []; message: string; failed: true }

export function requestMailto(r: AccessRequestResult): string | null {
  if (!r.contact) return null
  const folder = [r.driveName, r.folderName].filter(Boolean).join(' / ') || 'a folder'
  const subject = `RMS access request: ${folder}`
  const lines = [
    `Hello ${r.contact.name.replace(/ \(interim\)$/, '')},`,
    '',
    r.contact.interim
      ? `I have requested access to ${folder} in the RMS portal. The portal has no owner on file for this folder, so I am sending it to you as the interim RMS contact.`
      : `I have requested access to ${folder} in the RMS portal. You are listed as its owner.`,
    '',
    `Folder: ${r.folderLink ?? '(link not available)'}`,
    'Why I need it: (write your reason here)',
    r.requestId ? `Request ID: ${r.requestId}` : '',
    '',
    'If you agree, please add me to the right access group in Google Groups, or tell me who can.',
    'Thank you.',
  ].filter((l, i, a) => !(l === '' && a[i - 1] === ''))
  return `mailto:${r.contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`
}

export function RequestOutcomeBlock({ outcome }: { outcome: RequestOutcome }) {
  if ('failed' in outcome) {
    return (
      <>
        <Pill tone="amber">Not recorded</Pill>
        <p style={{ marginTop: 'var(--s-sm)', marginBottom: 0 }}>{outcome.message}</p>
      </>
    )
  }
  if (outcome.ok) {
    return (
      <>
        <Pill tone="accent">Request sent</Pill>
        <p style={{ marginTop: 'var(--s-sm)', marginBottom: 0 }}>
          The folder owner has been notified.
        </p>
      </>
    )
  }
  const href = requestMailto(outcome)
  return (
    <>
      <Pill tone="neutral">Recorded, owner not yet told</Pill>
      <p style={{ marginTop: 'var(--s-sm)' }}>
        {outcome.contact
          ? <>The portal does not message owners automatically yet. Send the email below to{' '}
              <b>{outcome.contact.name}</b>{outcome.contact.interim && ' (interim RMS contact: this folder has no owner on file)'}.
              It is already written: add why you need the folder, then send.</>
          : outcome.message}
      </p>
      {href && (
        <a className="btn btn--primary" href={href}>
          Email {outcome.contact!.name.replace(/ \(interim\)$/, '')}
        </a>
      )}
      {outcome.contact && (
        <p className="note" style={{ marginTop: 'var(--s-sm)', marginBottom: 0 }}>
          If the button does not open your email, write to {outcome.contact.email}.
        </p>
      )}
      {outcome.queued && (
        <p className="note" style={{ marginTop: 'var(--s-sm)', marginBottom: 0 }}>
          The RMS team can also see this request.
        </p>
      )}
    </>
  )
}
